import React from "react";
import _ from "lodash";
import cx from "classnames";

import ControlGroup from "./base/ControlGroup";
import ControlLabel from "./base/ControlLabel";
import InputControl from "./base/InputControl";
import ControlAddon from "./base/ControlAddon";
import ControlStatic from "./base/ControlStatic";
import AppConstants from "../constants/AppConstants";
import {
  INPUT_TYPE_RADIO,
  INPUT_TYPE_CHECKBOX,
  INPUT_TYPE_COLOR,
  INPUT_TYPE_TEXT,
  INPUT_TYPE_SELECT,
  INPUT_TYPE_THEME_COLORS,
  INPUT_TYPE_TEXT_AREA,
  INPUT_TYPE_NUMBER,
} from "../constants/ElementConstants";

import ColorPicker from "./base/ColorPicker";
import ThemeColorsColorPicker from "./base/ThemeColorsColorPicker";

import HelpTooltip from "./HelpTooltip";
import When from "./utils/When";
import { setupHandleWheel } from "../utils/InputUtils";
import slugify from "../utils/SlugifyUtils";
import uiPrefix from "../utils/UiPrefixUtils";
import { ToggleSyncContextMenu } from "./ToggleSyncContextMenu";

const { PARSE_INT_DEFAULT_RADIX, INPUT_BASE_CLASS, INPUT_SOURCE_PROPERTY_INPUT } = AppConstants;

class PropertyInput extends React.Component {
  static defaultProps = {
    baseClassName: INPUT_BASE_CLASS,
    hideLabel: false,
    "data-e2e": "",
    inputElementClassName: uiPrefix("input-element-class"),
    updateOnEnter: true,
    updateOnBlur: true,
    updateOnChange: true,
    updateOnKeyDown: false,
    updateOnUnchangedValue: false,
    onValidate: false,
    isRequired: false,
    error: false,
    helpTooltip: false,
    showPalette: true,
    showAlpha: true,
    showPaletteOnly: false,
    appendTo: "body",
  };

  constructor(props) {
    super(props);

    let { updateOnBlur, updateOnChange, updateOnEnter, message } = props;
    let valid = true;
    const value = this._initValue(props);

    if (
      props.type === INPUT_TYPE_SELECT ||
      props.type === INPUT_TYPE_CHECKBOX ||
      props.type === INPUT_TYPE_RADIO
    ) {
      updateOnBlur = false;
      updateOnEnter = false;
    } else if (props.type === INPUT_TYPE_TEXT_AREA) {
      updateOnEnter = false;
      updateOnChange = false;
    }

    if (props.error) {
      valid = false;
      message = props.error.message;
    }

    this.state = {
      value,
      initValue: value,
      valid,
      message,
      updateOnBlur,
      updateOnChange,
      updateOnEnter,
      focused: false,
      touched: false,
    };

    this.inputRef = React.createRef();
    this.sliderInputRef = React.createRef();

    this.id = _.uniqueId("si-prop_");

    this.getValue = this.getValue.bind(this);
    this.isValid = this.isValid.bind(this);
    this.triggerFocus = this.triggerFocus.bind(this);
    this.triggerBlur = this.triggerBlur.bind(this);
    this.triggerSelect = this.triggerSelect.bind(this);

    this._onBlur = this._onBlur.bind(this);
    this._onFocus = this._onFocus.bind(this);
    this._onChange = this._onChange.bind(this);
    this._onKeyDown = this._onKeyDown.bind(this);

    this._checkMinMax = this._checkMinMax.bind(this);
    this._getPropInfo = this._getPropInfo.bind(this);
    this._notifyChange = this._notifyChange.bind(this);
    this._sanitizeValue = this._sanitizeValue.bind(this);
    this._trimValue = this._trimValue.bind(this);
    this._updateValue = this._updateValue.bind(this);
    this._validateValue = this._validateValue.bind(this);
    this._wheelHandler = this._wheelHandler.bind(this);
    this.throttledNotify = _.throttle(this._notifyChange, 40);
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    const value = this._initValue(nextProps);

    const nextState = {
      value,
      initValue: value,
    };

    if (nextProps.error !== this.props.error) {
      if (nextProps.error === false) {
        nextState.valid = true;
        nextState.message = "";
      } else {
        nextState.valid = false;
        nextState.message = nextProps.error.message;
      }
    }

    this.setState(nextState);
  }

  // Increases the value on scroll over number inputs
  _wheelHandler(e) {
    const { value } = this.state;
    const handler = setupHandleWheel(
      this.props,
      (newVal, e) => this._updateValue(newVal, e),
      value
    );
    handler(e);
  }

  componentDidMount() {
    const numberInputEl = this.inputRef?.current?.element;
    if (this.props.type === "number" && numberInputEl && numberInputEl.addEventListener) {
      numberInputEl.addEventListener("wheel", this._wheelHandler);
      if (this.sliderInputRef.current) {
        this.sliderInputRef.current.addEventListener("wheel", this._wheelHandler);
      }
    }
  }

  componentWillUnmount() {
    const numberInputRef = this.inputRef?.current?.element;
    if (this.props.type === "number" && numberInputRef && numberInputRef.addEventListener) {
      numberInputRef.removeEventListener("wheel", this._wheelHandler);
      if (this.sliderInputRef.current) {
        this.sliderInputRef.current.removeEventListener("wheel", this._wheelHandler);
      }
    }
  }

  _initValue(props) {
    const { type } = props;
    let { value } = props;

    if (INPUT_TYPE_CHECKBOX === type || INPUT_TYPE_RADIO === type) {
      if (props.checked) {
        value = true;
      } else {
        value = false;
      }
    } else {
      if (props.value === undefined || props.value === null) {
        value = "";
      }
    }

    return value;
  }

  getValue() {
    return this.state.value;
  }

  isValid() {
    return this.state.valid;
  }

  triggerFocus() {
    if (this.inputRef.current.element) {
      this.inputRef.current.element.focus();
    }
  }

  triggerBlur() {
    if (this.inputRef.current.element) {
      this.inputRef.current.element.blur();
    }
  }

  triggerSelect() {
    if (this.inputRef.current.element) {
      this.inputRef.current.element.select();
    }
  }

  _onKeyDown(e) {
    const { updateOnEnter, value } = this.state;
    if (updateOnEnter && e.key === "Enter") {
      if (this.inputRef.current.element) {
        this.inputRef.current.element.blur();
      }
      this._notifyChange(this._formatSaveValue(value), e, true);
    }
  }

  _isText() {
    const { type } = this.props;
    return INPUT_TYPE_TEXT === type;
  }

  _onBlur(e) {
    this.setState({ focused: false });
    const { updateOnBlur, value } = this.state;
    if (updateOnBlur || this._isText()) {
      const updateValue = this._formatSaveValue(value);

      this._notifyChange(updateValue, e, true);
    }
  }

  _onFocus() {
    this.setState({ focused: true });
  }

  _formatSaveValue(value) {
    let updateValue = value;
    if (_.isString(value) && this._isText()) {
      updateValue = updateValue.trim();
      value.endsWith("-") && (updateValue = updateValue.slice(0, updateValue.length - 1));
    }
    if (this.props.type === INPUT_TYPE_NUMBER && updateValue === "") {
      this.setState({ value: this.state.initValue });
      updateValue = this.state.initValue;
    }
    return updateValue;
  }

  _notifyChange(value, e, shouldNotify = false, addUndoOverride = false) {
    const { disallowWhiteSpace, type } = this.props;
    value = this._isText() && !disallowWhiteSpace ? value : this._sanitizeValue(value);

    const isRealTimePreview =
      type === INPUT_TYPE_COLOR || type === INPUT_TYPE_THEME_COLORS || type === INPUT_TYPE_NUMBER;
    const data = {
      name: this.props.propName,
      value,
      origValue: this.state.initValue,
      addUndo: addUndoOverride || !this.state.touched || !isRealTimePreview,
      source: INPUT_SOURCE_PROPERTY_INPUT,
    };
    if (!this.state.touched) {
      this.setState({
        touched: true,
      });
    }

    if (type === INPUT_TYPE_CHECKBOX) {
      if (_.has(this.props, "valueMap")) {
        if (value) {
          data.value = _.get(this.props, ["valueMap", "checked"], 1);
        } else {
          data.value = _.get(this.props, ["valueMap", "unchecked"], 0);
        }

        if (this.state.origValue) {
          data.origValue = _.get(this.props, ["valueMap", "checked"], 1);
        } else {
          data.origValue = _.get(this.props, ["valueMap", "unchecked"], 0);
        }
      }
    }

    if (_.has(this.props, ["valueMap", "default"])) {
      data.default = _.get(this.props, ["valueMap", "default"]);
    }

    this.setState({ value, initValue: value });

    if (
      this.props.onChange &&
      ((type !== INPUT_TYPE_TEXT && type !== INPUT_TYPE_TEXT_AREA) || shouldNotify)
    ) {
      this.props.onChange(data, e);
    }
  }

  _getPropInfo(value) {
    return {
      name: this.props.propName,
      value,
      origValue: this.state.initValue,
    };
  }

  _onChange(e) {
    let { value } = e.target;
    const { type } = this.props;

    switch (type) {
      case INPUT_TYPE_CHECKBOX:
      case INPUT_TYPE_RADIO:
        value = this.inputRef.current.element.checked;
        break;
      default:
        break;
    }

    this._updateValue(value, e);
  }

  _updateValue(value, e, makeInputClean) {
    const { disallowWhiteSpace, onValueChange, type } = this.props;
    const { updateOnChange, value: prevValue } = this.state;

    makeInputClean && this.setState({ touched: false });
    value = disallowWhiteSpace ? this._trimValue(value) : value;
    const addUndoOverride = makeInputClean && type === INPUT_TYPE_THEME_COLORS;

    this._validateValue(value);

    if (value !== prevValue) {
      this.setState({ value });

      onValueChange && onValueChange(this._getPropInfo(value), e);

      const isEmptyNumber = value === "" && INPUT_TYPE_NUMBER === type;
      if (updateOnChange && !isEmptyNumber) {
        this.throttledNotify(value, e, false, addUndoOverride);
      }
    }
  }

  _sanitizeValue(value) {
    const { isAngle, propType } = this.props;
    if (INPUT_TYPE_NUMBER === this.props.propType) {
      value = parseFloat(value);
      value = this._checkMinMax(value);
    } else if (propType === "int") {
      value = parseInt(value, PARSE_INT_DEFAULT_RADIX);
      value = this._checkMinMax(value);
    } else if (value !== null && _.isString(value)) {
      value = value.trim();
    }

    // if input unit is degrees, clamp it from 0-359
    if (isAngle && (value > 360 || value < 0)) {
      value = Math.abs(value < 0 ? 360 + (value % 360) : value % 360);
    }

    if (_.isNaN(value)) {
      value = "";
    }

    return value;
  }

  _checkMinMax(value) {
    if (this.props.min !== undefined) {
      value = Math.max(this.props.min, value);
    }

    if (this.props.max) {
      value = Math.min(this.props.max, value);
    }

    return value;
  }

  _trimValue(value) {
    if (this.props.type !== INPUT_TYPE_TEXT_AREA && this.props.propType !== INPUT_TYPE_TEXT) {
      if (value != null && !_.isBoolean(value) && value.length > 0) {
        value = value.trim();
      }
    }
    return value;
  }

  _validateValue(value) {
    let { valid } = this.state;

    if (this.props.onValidate) {
      const data = {
        name: this.props.propName,
        label: this.props.label,
        value,
        origValue: this.state.initValue,
      };
      const result = this.props.onValidate(data);

      valid = _.get(result, "valid", false);
      if (!valid) {
        this.setState({ valid: false, message: result.message });
      } else {
        this.setState({ valid: true, message: "" });
      }
    }

    return valid;
  }

  render() {
    const {
      addonAfter,
      appendTo,
      baseClassName,
      className,
      inputElementClassName,
      "data-e2e": dataE2E,
      charCount,
      checked,
      children,
      classes,
      columns,
      disabled,
      help,
      helpTooltip,
      hideLabel,
      id,
      includeSlider,
      isRequired,
      label,
      layoutClass,
      layout,
      labelStyle,
      max,
      min,
      noPad,
      onChange,
      onValueChange,
      palette,
      parentProp,
      pickerWidth,
      placeholder,
      propType,
      showPalette,
      showAlpha,
      showPaletteOnly,
      sliderMin,
      sliderMax,
      sliderStep,
      step,
      style,
      sync,
      syncLayout,
      toggleSync,
      type,
      updateOnEnter,
      updateOnBlur,
      value,
      ...other
    } = this.props;

    let Component = InputControl;
    const groupStyle = {};

    let componentProps = {
      value: this.state.value,
      onChange: this._onChange,
      onBlur: this._onBlur,
      onFocus: this._onFocus,
      onKeyPress: this._onKeyDown,
      ref: this.inputRef,
      id: this.id,
      type,
      disabled,
      step,
      min,
      max,
      placeholder,
    };
    const splitInput = sync !== undefined && !sync;

    let errorMessage = null;

    let propInputClasses = "layout-default";

    switch (type) {
      case INPUT_TYPE_TEXT_AREA:
        propInputClasses = "layout-stack";

        break;
      case INPUT_TYPE_RADIO:
      case INPUT_TYPE_CHECKBOX:
        componentProps.checked = checked;
        componentProps.value = "";

        propInputClasses = "layout-control-first";

        break;
      case INPUT_TYPE_THEME_COLORS:
      case INPUT_TYPE_COLOR:
        componentProps = {
          color: this.state.value,
          showPalette,
          showPaletteOnly,
          showAlpha,
          width: pickerWidth,
          appendTo,
          disabled,
          palette,
          id: this.id,
          onColorChange: this._updateValue,
        };

        if (INPUT_TYPE_COLOR === type) {
          Component = ColorPicker;
          componentProps.allowEmpty = showAlpha;
          componentProps.label = label;
        } else {
          Component = ThemeColorsColorPicker;
        }

        break;
      default:
        break;
    }

    // override any previous layouts with the one provided
    if (layoutClass) {
      propInputClasses = layoutClass;
    }

    if (layout) {
      propInputClasses = `layout-${layout}`;
    }

    if (_.isNumber(columns)) {
      groupStyle.gridTemplateColumns = columns;
    }

    if (!this.state.valid) {
      errorMessage = this.state.message;
      propInputClasses += ` ${baseClassName}--error`;
    }

    if (!this.state.valid) {
      errorMessage = this.state.message;
      propInputClasses += ` ${baseClassName}--error`;
    }

    const groupClassNames = cx([
      propInputClasses,
      { disabled },
      { split: splitInput },
      { splittable: sync !== undefined },
      { "no-pad": noPad },
      className,
    ]);

    return (
      <ControlGroup
        baseClassName={baseClassName}
        data-e2e={dataE2E}
        layout={syncLayout}
        toggleSync={toggleSync}
        label={!hideLabel ? label : ""}
        split={splitInput}
        className={groupClassNames}
        style={{ ...groupStyle, ...style }}
      >
        <Component
          data-e2e={`${inputElementClassName}-${slugify(label)}`}
          baseClassName={`${inputElementClassName}-${slugify(label)} si-c-control`}
          {...componentProps}
        >
          {children}
        </Component>
        <When it={includeSlider}>
          <div className="si-c-slider">
            <input
              {...{
                ...componentProps,
                ref: this.sliderInputRef,
                type: "range",
                min: sliderMin || min,
                max: sliderMax || parseFloat(step || 1) * 100,
                step: sliderStep || parseFloat(step || 1),
              }}
              data-e2e={uiPrefix("number-slider-component")}
              className={`${uiPrefix("number-slider-component")} si-c-control ${
                disabled ? " disabled" : ""
              }`}
              data-notifyonchange
            />
          </div>
        </When>{" "}
        <When it={label && !hideLabel}>
          {helpTooltip ? (
            <HelpTooltip
              baseClassName="si-c-property-input__help"
              trigger="click"
              tooltipKey={helpTooltip}
              alignment="left"
            >
              <ControlLabel
                htmlFor={this.id}
                text={`${label}${isRequired ? " *" : ""}`}
                className="common"
                style={labelStyle}
              />
            </HelpTooltip>
          ) : (
            <ControlLabel
              htmlFor={this.id}
              text={`${label}${isRequired ? " *" : ""}`}
              className="common"
              style={labelStyle}
            />
          )}
        </When>
        {addonAfter && !this.state.focused && (
          <ControlAddon
            baseClassName="si-c-property-input__unit"
            className={includeSlider ? "slider" : ""}
          >
            {addonAfter}
          </ControlAddon>
        )}
        {charCount && charCount(componentProps.value.length)}
        <ToggleSyncContextMenu sync={sync} layout={syncLayout} toggleSync={toggleSync} />
        {errorMessage && (
          <ControlStatic className="si-c-property-input__error" text={errorMessage} />
        )}
      </ControlGroup>
    );
  }
}

export default PropertyInput;
